import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { TimeEntry } from '../models/time-entry.model';

export interface StartTimerRequest {
  projectId: number;
  taskId?: number;
  description: string;
}

export interface ActiveTimerResponse {
  hasActiveTimer: boolean;
  timeEntry: TimeEntry | null;
}

export interface MarkAsInvoicedRequest {
  timeEntryIds: number[];
  invoiceId: number;
}

export interface TotalHoursResponse {
  projectId: number;
  totalHours: number;
}

export interface TotalBillableResponse {
  projectId: number;
  totalBillableAmount: number;
}

@Injectable({
  providedIn: 'root'
})
export class TimeEntryService {
  private readonly apiUrl = `${environment.apiUrl}/TimeEntry`;

  constructor(private http: HttpClient) {}

  // Get all time entries for current user
  getAllTimeEntries(): Observable<TimeEntry[]> {
    return this.http.get<TimeEntry[]>(this.apiUrl, { withCredentials: true });
  }

  // Get time entry by ID
  getTimeEntryById(id: number): Observable<TimeEntry> {
    return this.http.get<TimeEntry>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // Get time entries by project
  getTimeEntriesByProject(projectId: number): Observable<TimeEntry[]> {
    return this.http.get<TimeEntry[]>(`${this.apiUrl}/project/${projectId}`, { withCredentials: true });
  }

  // Get time entries by date range
  getTimeEntriesByDateRange(startDate: Date, endDate: Date): Observable<TimeEntry[]> {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http.get<TimeEntry[]>(`${this.apiUrl}/range`, { params, withCredentials: true });
  }

  // Get active timer
  getActiveTimeEntry(): Observable<ActiveTimerResponse> {
    return this.http.get<ActiveTimerResponse>(`${this.apiUrl}/active`, { withCredentials: true });
  }

  // Start timer
  startTimer(request: StartTimerRequest): Observable<TimeEntry> {
    return this.http.post<TimeEntry>(`${this.apiUrl}/start`, request, { withCredentials: true });
  }

  // Stop timer
  stopTimer(id: number): Observable<TimeEntry> {
    return this.http.post<TimeEntry>(`${this.apiUrl}/stop/${id}`, {}, { withCredentials: true });
  }

  // Add manual time entry
  addManualTimeEntry(timeEntry: TimeEntry): Observable<TimeEntry> {
    return this.http.post<TimeEntry>(this.apiUrl, timeEntry, { withCredentials: true });
  }

  // Update time entry
  updateTimeEntry(id: number, timeEntry: Partial<TimeEntry>): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}`, timeEntry, { withCredentials: true });
  }

  // Delete time entry
  deleteTimeEntry(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // Get unbilled time entries
  getUnbilledTimeEntries(): Observable<TimeEntry[]> {
    return this.http.get<TimeEntry[]>(`${this.apiUrl}/unbilled`, { withCredentials: true });
  }

  // Get unbilled time entries for a specific project, optionally filtered by task IDs
  getUnbilledTimeEntriesByProject(projectId: number, taskIds?: number[]): Observable<TimeEntry[]> {
    let params = new HttpParams();
    if (taskIds && taskIds.length > 0) {
      params = params.set('taskIds', taskIds.join(','));
    }
    return this.http.get<TimeEntry[]>(`${this.apiUrl}/unbilled/project/${projectId}`, { params, withCredentials: true });
  }

  // Get time entries by invoice
  getTimeEntriesByInvoice(invoiceId: number): Observable<TimeEntry[]> {
    return this.http.get<TimeEntry[]>(`${this.apiUrl}/invoice/${invoiceId}`, { withCredentials: true });
  }

  // Get total hours by project
  getTotalHoursByProject(projectId: number): Observable<TotalHoursResponse> {
    return this.http.get<TotalHoursResponse>(`${this.apiUrl}/project/${projectId}/hours`, { withCredentials: true });
  }

  // Get total billable amount by project
  getTotalBillableAmountByProject(projectId: number): Observable<TotalBillableResponse> {
    return this.http.get<TotalBillableResponse>(`${this.apiUrl}/project/${projectId}/billable`, { withCredentials: true });
  }

  // Mark time entries as invoiced
  markTimeEntriesAsInvoiced(request: MarkAsInvoicedRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/mark-invoiced`, request, { withCredentials: true });
  }
}
